<p-confirmDialog />
<div class="space-y-6 animate-in fade-in duration-500" [attr.data-lang]="currentLang()">
  <!-- Header -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 italic">
        {{ 'EMPLOYEES.TITLE' | translate }}
      </h1>
      <p class="text-gray-600 dark:text-gray-400 mt-2">{{ 'EMPLOYEES.SUBTITLE' | translate }}</p>
    </div>
    <button
      (click)="openCreate()"
      class="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all shadow-md active:scale-95 cursor-pointer"
    >
      <lucide-icon [img]="Plus" class="w-4 h-4 mr-2"></lucide-icon>
      {{ 'EMPLOYEES.ADD' | translate }}
    </button>
  </div>

  <!-- Stats -->
  <div class="grid gap-4 grid-cols-2 lg:grid-cols-4">
    <div
      class="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow"
    >
      <div class="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ 'EMPLOYEES.STATS.TOTAL' | translate }}
      </div>
      <div
        class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2 tabular-nums"
      >
        {{ stats().total }}
      </div>
    </div>
    <div
      class="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow"
    >
      <div class="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ 'EMPLOYEES.STATS.ACTIVE' | translate }}
      </div>
      <div
        class="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-2 tabular-nums"
      >
        {{ stats().active }}
      </div>
    </div>
    <div
      class="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow"
    >
      <div class="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ 'EMPLOYEES.STATS.MANAGERS' | translate }}
      </div>
      <div
        class="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mt-2 tabular-nums"
      >
        {{ stats().managers }}
      </div>
    </div>
    <div
      class="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow"
    >
      <div class="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ 'EMPLOYEES.STATS.ASSOCIATES' | translate }}
      </div>
      <div
        class="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mt-2 tabular-nums"
      >
        {{ stats().associates }}
      </div>
    </div>
  </div>

  <!-- Search -->
  <div
    class="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm"
  >
    <div class="flex flex-col sm:flex-row gap-4">
      <div class="relative flex-1">
        <lucide-icon
          [img]="Search"
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
        ></lucide-icon>
        <input
          type="text"
          [placeholder]="'EMPLOYEES.SEARCH' | translate"
          [(ngModel)]="searchQuery"
          (keyup.enter)="onSearch()"
          class="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all dark:text-gray-100"
        />
      </div>
      <button
        (click)="onSearch()"
        class="inline-flex items-center px-4 py-2 border border-gray-200 dark:border-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
      >
        <lucide-icon [img]="Search" class="w-4 h-4 mr-2"></lucide-icon>
        {{ 'COMMON.SEARCH' | translate }}
      </button>
    </div>
  </div>

  <!-- Employee Grid -->
  @if (loading()) {
    <div class="flex items-center justify-center py-16 text-gray-400">
      <div
        class="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-3"
      ></div>
      {{ 'EMPLOYEES.LOADING' | translate }}
    </div>
  } @else {
    <div class="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      @for (employee of employeeList(); track employee.userId) {
        <div
          class="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg transition-all duration-300"
        >
          <div class="p-6">
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-4">
                <div
                  class="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg shadow-inner"
                >
                  {{ getInitials(employee.fullName) }}
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900 dark:text-gray-100">
                    {{ employee.fullName }}
                  </h3>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 max-w-[160px] truncate">
                    {{ getPermissionSummary(employee.permissions) }}
                  </p>
                </div>
              </div>

              <!-- 3-dot menu -->
              <div class="relative">
                <button
                  (click)="toggleMenu(employee.userId, $event)"
                  class="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                >
                  <lucide-icon [img]="MoreVertical" class="w-4 h-4"></lucide-icon>
                </button>
                @if (activeMenuId() === employee.userId) {
                  <!-- eslint-disable-next-line -->
                  <div
                    (click)="$event.stopPropagation()"
                    class="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-100 dark:border-gray-800 z-20 animate-in fade-in zoom-in-95 duration-200"
                  >
                    <button
                      (click)="openEdit(employee); activeMenuId.set(null)"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center transition-colors rounded-t-lg"
                    >
                      <lucide-icon [img]="Edit" class="w-3 h-3 mr-2"></lucide-icon>
                      {{ 'EMPLOYEES.MENU.EDIT' | translate }}
                    </button>
                    <button
                      (click)="openAssignStores(employee); activeMenuId.set(null)"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center transition-colors"
                    >
                      <lucide-icon [img]="Building2" class="w-3 h-3 mr-2"></lucide-icon>
                      {{ 'EMPLOYEES.MENU.ASSIGN_STORES' | translate }}
                    </button>
                    <button
                      (click)="openPermDialog(employee); activeMenuId.set(null)"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center transition-colors"
                    >
                      <lucide-icon [img]="Lock" class="w-3 h-3 mr-2"></lucide-icon>
                      {{ 'EMPLOYEES.MENU.PERMISSIONS' | translate }}
                    </button>
                    <button
                      (click)="openScheduleDialog(employee); activeMenuId.set(null)"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center transition-colors"
                    >
                      <lucide-icon [img]="CalendarDays" class="w-3 h-3 mr-2"></lucide-icon>
                      {{ 'EMPLOYEES.MENU.SCHEDULE' | translate }}
                    </button>
                    <div class="border-t border-gray-100 dark:border-gray-800"></div>
                    <button
                      (click)="deleteEmployee(employee); activeMenuId.set(null)"
                      class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center transition-colors rounded-b-lg"
                    >
                      <lucide-icon [img]="Trash2" class="w-3 h-3 mr-2"></lucide-icon>
                      {{ 'EMPLOYEES.MENU.DELETE' | translate }}
                    </button>
                  </div>
                }
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <lucide-icon [img]="Phone" class="w-4 h-4"></lucide-icon>
                {{ employee.phoneNumber || '—' }}
              </div>
              <div class="pt-1">
                <span
                  [class]="
                    employee.isActive
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                      : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                  "
                  class="px-2.5 py-0.5 text-xs font-semibold rounded-full border"
                >
                  {{
                    employee.isActive
                      ? ('EMPLOYEES.STATUS.ACTIVE' | translate)
                      : ('EMPLOYEES.STATUS.INACTIVE' | translate)
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      } @empty {
        <div
          class="col-span-full py-12 text-center bg-gray-50 dark:bg-gray-950 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800"
        >
          <p class="text-gray-500 dark:text-gray-400">{{ 'EMPLOYEES.EMPTY' | translate }}</p>
        </div>
      }
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
      <div class="flex items-center justify-between gap-2 flex-wrap">
        <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          {{
            'EMPLOYEES.PAGINATION'
              | translate
                : { from: (currentPage() - 1) * pageSize + 1, to: pageEnd, total: totalCount() }
          }}
        </span>
        <div class="flex items-center gap-1">
          <button
            (click)="goPage(currentPage() - 1)"
            [disabled]="currentPage() === 1"
            class="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <lucide-icon [img]="ChevronLeft" class="w-4 h-4"></lucide-icon>
          </button>
          <span class="sm:hidden px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ currentPage() }} / {{ totalPages }}
          </span>
          @for (p of pageRange; track p) {
            <button
              (click)="goPage(p)"
              [class.bg-green-600]="currentPage() === p"
              [class.text-white]="currentPage() === p"
              [class.border-green-600]="currentPage() === p"
              class="hidden sm:flex w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
            >
              {{ p }}
            </button>
          }
          <button
            (click)="goPage(currentPage() + 1)"
            [disabled]="currentPage() === totalPages"
            class="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <lucide-icon [img]="ChevronRight" class="w-4 h-4"></lucide-icon>
          </button>
        </div>
      </div>
    }
  }
</div>

<!-- ─── Create / Edit Dialog ──────────────────────────────────────────────────── -->
@if (formVisible) {
  <!-- eslint-disable-next-line -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
    (click)="closeForm()"
  >
    <!-- eslint-disable-next-line -->
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md flex flex-col max-h-[90vh]"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {{
            isEditMode
              ? ('EMPLOYEES.FORM.EDIT_TITLE' | translate)
              : ('EMPLOYEES.FORM.ADD_TITLE' | translate)
          }}
        </h2>
        <button
          (click)="closeForm()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      <!-- Form -->
      <div class="px-6 py-5 space-y-4 overflow-y-auto flex-1">
        <div>
          <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{{
            'EMPLOYEES.FORM.FULL_NAME' | translate
          }}</label>
          <input
            type="text"
            [(ngModel)]="empForm.fullName"
            [placeholder]="'EMPLOYEES.FORM.FULL_NAME_HINT' | translate"
            class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
          />
        </div>

        @if (!isEditMode) {
          <div>
            <label
              for="emp-phone"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >{{ 'EMPLOYEES.FORM.PHONE' | translate }}</label
            >
            <div
              class="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-green-500 bg-gray-50 dark:bg-gray-950"
            >
              <select
                [(ngModel)]="empPhoneCode"
                [ngModelOptions]="{ standalone: true }"
                class="shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm pl-2 pr-1 border-r border-gray-200 dark:border-gray-700 outline-none cursor-pointer"
              >
                <option value="+998">🇺🇿 +998</option>
                <option value="+7">🇷🇺 +7</option>
                <option value="+992">🇹🇯 +992</option>
                <option value="+993">🇹🇲 +993</option>
                <option value="+996">🇰🇬 +996</option>
                <option value="+994">🇦🇿 +994</option>
              </select>
              <input
                id="emp-phone"
                type="tel"
                [(ngModel)]="empForm.phoneNumber"
                placeholder="90 123 45 67"
                class="flex-1 min-w-0 px-3 py-2 bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label
              for=""
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >{{ 'EMPLOYEES.FORM.PASSWORD' | translate }}</label
            >
            <div class="relative">
              <input
                [type]="showPassword ? 'text' : 'password'"
                [(ngModel)]="empForm.password"
                [placeholder]="'EMPLOYEES.FORM.PASSWORD_HINT' | translate"
                class="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
              />
              <button
                type="button"
                (click)="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <lucide-icon [img]="showPassword ? EyeOff : Eye" class="w-4 h-4"></lucide-icon>
              </button>
            </div>
          </div>
        }

        @if (isEditMode) {
          <div>
            <label
              for="emp-edit-phone"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >{{ 'EMPLOYEES.FORM.PHONE' | translate }}</label
            >
            <input
              id="emp-edit-phone"
              type="tel"
              [(ngModel)]="empForm.phoneNumber"
              placeholder="+998901234567"
              class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          <div>
            <label
              for="emp-edit-password"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >{{ 'EMPLOYEES.FORM.PASSWORD' | translate }}</label
            >
            <div class="relative">
              <input
                id="emp-edit-password"
                [type]="showPassword ? 'text' : 'password'"
                [(ngModel)]="empForm.password"
                [placeholder]="'EMPLOYEES.FORM.PASSWORD_EDIT_HINT' | translate"
                class="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
              />
              <button
                type="button"
                (click)="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <lucide-icon [img]="showPassword ? EyeOff : Eye" class="w-4 h-4"></lucide-icon>
              </button>
            </div>
          </div>

          <div
            class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800"
          >
            <div>
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ 'EMPLOYEES.FORM.STATUS' | translate }}
              </p>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ 'EMPLOYEES.FORM.STATUS_HINT' | translate }}
              </p>
            </div>
            <button
              type="button"
              (click)="empForm.isActive = !empForm.isActive"
              [class]="empForm.isActive ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            >
              <span
                [class]="empForm.isActive ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              ></span>
            </button>
          </div>
        }
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0"
      >
        <button
          (click)="closeForm()"
          [disabled]="saving"
          class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 transition-colors"
        >
          {{ 'EMPLOYEES.FORM.CANCEL' | translate }}
        </button>
        <button
          (click)="submitForm()"
          [disabled]="saving"
          class="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg disabled:opacity-50 transition-colors"
        >
          {{
            saving
              ? ('EMPLOYEES.FORM.SAVING' | translate)
              : isEditMode
                ? ('EMPLOYEES.FORM.SAVE_BTN' | translate)
                : ('EMPLOYEES.FORM.ADD_BTN' | translate)
          }}
        </button>
      </div>
    </div>
  </div>
}

<!-- ─── PIN Notification ───────────────────────────────────────────────────────── -->
@if (createdPin) {
  <!-- eslint-disable-next-line -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    (click)="createdPin = null"
  >
    <!-- eslint-disable-next-line -->
    <div
      class="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6 text-center"
      (click)="$event.stopPropagation()"
    >
      <div
        class="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mx-auto mb-4"
      >
        <svg
          class="w-7 h-7 text-emerald-600"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {{ 'EMPLOYEES.PIN.TITLE' | translate }}
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {{ 'EMPLOYEES.PIN.TEXT' | translate: { name: createdName } }}
      </p>
      <div
        class="text-3xl font-mono font-bold tracking-[0.3em] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-xl py-3 px-4 mb-5"
      >
        {{ createdPin }}
      </div>
      <p class="text-xs text-gray-400 mb-5">{{ 'EMPLOYEES.PIN.HINT' | translate }}</p>
      <button
        (click)="createdPin = null"
        class="w-full py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors"
      >
        {{ 'EMPLOYEES.PIN.OK' | translate }}
      </button>
    </div>
  </div>
}

<!-- ─── Assign Stores Dialog ───────────────────────────────────────────────────── -->
@if (assignDialogVisible) {
  <!-- eslint-disable-next-line -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
    (click)="closeAssignDialog()"
  >
    <!-- eslint-disable-next-line -->
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-sm flex flex-col max-h-[90vh]"
      (click)="$event.stopPropagation()"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0"
      >
        <div>
          <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
            {{ 'EMPLOYEES.ASSIGN.TITLE' | translate }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ assigningEmployee?.fullName }}
          </p>
        </div>
        <button
          (click)="closeAssignDialog()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      <div class="px-6 py-4">
        <p-multiselect
          [(ngModel)]="selectedStoreIds"
          [options]="stores()"
          optionLabel="name"
          optionValue="id"
          [placeholder]="'EMPLOYEES.ASSIGN.PLACEHOLDER' | translate"
          appendTo="body"
          [showClear]="true"
          display="chip"
          styleClass="w-full"
        ></p-multiselect>
      </div>

      <div
        class="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0"
      >
        <span class="text-xs text-gray-400">{{
          'EMPLOYEES.ASSIGN.SELECTED' | translate: { count: selectedStoreIds.length }
        }}</span>
        <div class="flex gap-3">
          <button
            (click)="closeAssignDialog()"
            [disabled]="assigning"
            class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 transition-colors"
          >
            {{ 'EMPLOYEES.ASSIGN.CANCEL' | translate }}
          </button>
          <button
            (click)="saveAssignStores()"
            [disabled]="assigning"
            class="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {{
              assigning
                ? ('EMPLOYEES.ASSIGN.SAVING' | translate)
                : ('EMPLOYEES.ASSIGN.SAVE' | translate)
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Permission Dialog ──────────────────────────────────────────────────────── -->
@if (permDialogVisible) {
  <!-- eslint-disable-next-line -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
    (click)="closePermDialog()"
  >
    <!-- eslint-disable-next-line -->
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col"
      (click)="$event.stopPropagation()"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0"
      >
        <div class="flex items-center gap-3">
          <lucide-icon [img]="Lock" class="w-4 h-4 text-green-600"></lucide-icon>
          <div>
            <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
              {{ 'EMPLOYEES.PERM.TITLE' | translate }}
            </h2>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ permEmployee?.fullName }}
            </p>
          </div>
        </div>
        <button
          (click)="closePermDialog()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      <!-- Quick templates -->
      <div class="px-6 pt-4 shrink-0">
        <p
          class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2"
        >
          {{ 'EMPLOYEES.PERM.TEMPLATE' | translate }}
        </p>
        <div class="flex flex-wrap gap-2">
          @for (t of roleTemplates; track t.key) {
            <button
              (click)="applyTemplate(t)"
              class="px-3 py-1.5 text-xs font-medium rounded-lg border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
            >
              {{ t.nameKey | translate }}
            </button>
          }
        </div>
      </div>

      <!-- Permission groups -->
      <div class="px-6 py-4 overflow-y-auto flex-1 space-y-5">
        @for (group of permissionGroups; track group.group) {
          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {{ group.labelKey | translate }}
              </p>
              <button
                (click)="toggleGroupAll(group)"
                class="text-xs text-green-600 dark:text-green-400 hover:underline"
              >
                {{
                  isGroupFullySelected(group)
                    ? ('EMPLOYEES.PERM.DESELECT_ALL' | translate)
                    : ('EMPLOYEES.PERM.SELECT_ALL' | translate)
                }}
              </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              @for (perm of group.permissions; track perm.key) {
                <label
                  class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <input
                    type="checkbox"
                    [checked]="isPermSelected(perm.key)"
                    (change)="togglePerm(perm.key)"
                    class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span class="text-sm text-gray-700 dark:text-gray-300">{{
                    perm.labelKey | translate
                  }}</span>
                </label>
              }
            </div>
          </div>
        }
      </div>

      <div
        class="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0"
      >
        <span class="text-xs text-gray-400">{{
          'EMPLOYEES.PERM.SELECTED' | translate: { count: selectedPermissions.length }
        }}</span>
        <div class="flex gap-3">
          <button
            (click)="closePermDialog()"
            [disabled]="savingPerms"
            class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 transition-colors"
          >
            {{ 'EMPLOYEES.PERM.CANCEL' | translate }}
          </button>
          <button
            (click)="savePermissions()"
            [disabled]="savingPerms"
            class="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {{
              savingPerms
                ? ('EMPLOYEES.PERM.SAVING' | translate)
                : ('EMPLOYEES.PERM.SAVE' | translate)
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Work Schedule Dialog ───────────────────────────────────────────────────── -->
@if (scheduleDialogVisible) {
  <!-- eslint-disable-next-line -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
    (click)="closeScheduleDialog()"
  >
    <!-- eslint-disable-next-line -->
    <div
      class="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-sm flex flex-col max-h-[90vh]"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0"
      >
        <div>
          <h2
            class="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"
          >
            <lucide-icon [img]="CalendarDays" class="w-4 h-4 text-green-600"></lucide-icon>
            {{ 'EMPLOYEES.SCHEDULE.TITLE' | translate }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {{ scheduleEmployee?.fullName }}
          </p>
        </div>
        <button
          (click)="closeScheduleDialog()"
          class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
        >
          <lucide-icon [img]="X" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      @if (scheduleLoading()) {
        <div class="p-10 text-center text-gray-400">
          <div
            class="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"
          ></div>
          {{ 'EMPLOYEES.SCHEDULE.LOADING' | translate }}
        </div>
      } @else {
        <div class="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          <!-- Work days -->
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ 'EMPLOYEES.SCHEDULE.WORK_DAYS' | translate }}
            </p>
            <div class="flex gap-2 flex-wrap">
              @for (day of weekDays; track day.value) {
                <button
                  (click)="toggleWorkDay(day.value)"
                  [class]="
                    isWorkDay(day.value)
                      ? 'px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white border border-green-600'
                      : 'px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-green-400'
                  "
                >
                  {{ day.label }}
                </button>
              }
            </div>
          </div>

          <!-- Check-in time -->
          <div>
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <lucide-icon
                [img]="Clock"
                class="inline w-3.5 h-3.5 mr-1 text-green-500"
              ></lucide-icon>
              {{ 'EMPLOYEES.SCHEDULE.CHECK_IN' | translate }}
            </label>
            <p-datepicker
              [(ngModel)]="scheduleForm.checkInTime"
              [timeOnly]="true"
              hourFormat="24"
              class="w-full"
              appendTo="body"
            />
          </div>

          <!-- Check-out time -->
          <div>
            <label for="" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <lucide-icon
                [img]="Clock"
                class="inline w-3.5 h-3.5 mr-1 text-amber-500"
              ></lucide-icon>
              {{ 'EMPLOYEES.SCHEDULE.CHECK_OUT' | translate }}
            </label>
            <p-datepicker
              [(ngModel)]="scheduleForm.checkOutTime"
              [timeOnly]="true"
              hourFormat="24"
              class="w-full"
              appendTo="body"
            />
          </div>

          <!-- Info note -->
          <p
            class="text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2"
          >
            {{ 'EMPLOYEES.SCHEDULE.NOTE' | translate }}
          </p>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0"
        >
          <button
            (click)="closeScheduleDialog()"
            [disabled]="scheduleSaving()"
            class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 transition-colors"
          >
            {{ 'EMPLOYEES.FORM.CANCEL' | translate }}
          </button>
          <button
            (click)="saveSchedule()"
            [disabled]="scheduleSaving()"
            class="px-4 py-2 text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {{
              scheduleSaving()
                ? ('EMPLOYEES.SCHEDULE.SAVING' | translate)
                : ('EMPLOYEES.SCHEDULE.SAVE' | translate)
            }}
          </button>
        </div>
      }
    </div>
  </div>
}
